import { z } from 'zod';
import { DEFAULT_FULL_NOTIFICATION_ROLE_POLICY, DEFAULT_NON_REPORT_NOTIFICATION_ROLE_POLICY, DEFAULT_NOTIFICATION_PREFERENCES, ReportNotificationCapabilityIds, } from '../../app-notification';
import { BusinessAccessV2Schema, } from '../business-access-v2';
import { BusinessRoleDefinitionV2Schema, DefaultBusinessRoleIdV2, } from '../business-roles-v2';
import { BusinessActionEnum, BusinessProductAreaV2 } from './access-enums-v2';
const addOutOfBoundsGrantIssuesForBucket = (grants, allowed, roleIndex, bucket, context) => {
    const allowedValues = new Set(allowed);
    grants.forEach((grant, grantIndex) => {
        if (!allowedValues.has(grant)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: `${grant} is not enabled in businessAccess.${bucket}`,
                path: ['roleDefinitions', roleIndex, 'grants', bucket, grantIndex],
            });
        }
    });
};
const addOutOfBoundsGrantIssues = (role, roleIndex, businessAccess, context) => {
    addOutOfBoundsGrantIssuesForBucket(role.grants.productAreas, businessAccess.productAreas, roleIndex, 'productAreas', context);
    addOutOfBoundsGrantIssuesForBucket(role.grants.actions, businessAccess.actions, roleIndex, 'actions', context);
    addOutOfBoundsGrantIssuesForBucket(role.grants.paymentMethods, businessAccess.paymentMethods, roleIndex, 'paymentMethods', context);
};
const addReportNotificationPolicyIssues = (role, roleIndex, context) => {
    if (!role.notificationPolicy)
        return;
    const reportCapabilities = new Set(ReportNotificationCapabilityIds);
    const policyContainsReportCapability = [
        ...role.notificationPolicy.canReceive,
        ...role.notificationPolicy.canConfigure,
        ...role.notificationPolicy.visible,
    ].some((capabilityId) => reportCapabilities.has(capabilityId));
    if (!policyContainsReportCapability)
        return;
    const hasReportArea = role.grants.productAreas.includes(BusinessProductAreaV2.REPORTS);
    const hasReportView = role.grants.actions.includes(BusinessActionEnum.REPORT_VIEW);
    if (!hasReportArea || !hasReportView) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Report notification capabilities require REPORTS product access and REPORT_VIEW action access',
            path: ['roleDefinitions', roleIndex, 'notificationPolicy'],
        });
    }
};
/**
 * Complete business authorization model.
 *
 * Cross-field validation makes the business access list an enforceable ceiling,
 * validates sensitive actions, and prevents dangling fallback role references.
 */
export const BusinessAuthorizationV2Schema = z
    .object({
    businessAccess: BusinessAccessV2Schema,
    roleDefinitions: z.array(BusinessRoleDefinitionV2Schema).default([]),
})
    .superRefine(({ businessAccess, roleDefinitions }, context) => {
    const roleIds = new Set();
    roleDefinitions.forEach((role, roleIndex) => {
        if (roleIds.has(role.id)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Duplicate role id: ${role.id}`,
                path: ['roleDefinitions', roleIndex, 'id'],
            });
        }
        roleIds.add(role.id);
        addOutOfBoundsGrantIssues(role, roleIndex, businessAccess, context);
        addReportNotificationPolicyIssues(role, roleIndex, context);
    });
    businessAccess.policy.sensitiveActions.forEach((action, actionIndex) => {
        if (!businessAccess.actions.includes(action)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: `${action} is not enabled in businessAccess.actions`,
                path: ['businessAccess', 'policy', 'sensitiveActions', actionIndex],
            });
        }
    });
    const { fallbackRoleId } = businessAccess.policy;
    if (fallbackRoleId && !roleIds.has(fallbackRoleId)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Fallback role does not exist: ${fallbackRoleId}`,
            path: ['businessAccess', 'policy', 'fallbackRoleId'],
        });
    }
});
const selectEnabled = (enabled, requested) => {
    const requestedValues = new Set(requested);
    return enabled.filter((value) => requestedValues.has(value));
};
/**
 * Builds practical starter roles from a business's actual access ceiling.
 * Filtering at creation time keeps every generated role valid for that business.
 */
export const createDefaultBusinessRolesV2 = (businessAccess) => {
    const businessSupportsReports = businessAccess.productAreas.includes(BusinessProductAreaV2.REPORTS) &&
        businessAccess.actions.includes(BusinessActionEnum.REPORT_VIEW);
    const reportCapablePolicy = businessSupportsReports
        ? DEFAULT_FULL_NOTIFICATION_ROLE_POLICY
        : DEFAULT_NON_REPORT_NOTIFICATION_ROLE_POLICY;
    const ownerOnlyProductAreas = new Set([
        BusinessProductAreaV2.ADMIN,
        BusinessProductAreaV2.PLATFORM_OPS,
    ]);
    const managerProductAreas = businessAccess.productAreas.filter((area) => !ownerOnlyProductAreas.has(area));
    const managerActions = businessAccess.actions.filter((action) => action !== BusinessActionEnum.DEVICE_PAIR_APPROVE);
    const transactionViewActions = businessAccess.productAreas.some((area) => [
        BusinessProductAreaV2.TRANSACTIONS,
        BusinessProductAreaV2.TRANSACTIONS_V4,
    ].includes(area))
        ? [BusinessActionEnum.TRANSACTIONS_VIEW]
        : [];
    return [
        {
            id: DefaultBusinessRoleIdV2.OWNER,
            name: 'Owner',
            description: 'Full access within the business access ceiling.',
            grants: {
                productAreas: [...businessAccess.productAreas],
                actions: [...businessAccess.actions],
                paymentMethods: [...businessAccess.paymentMethods],
            },
            limits: {
                canRefundOwnTransactionsOnly: false,
            },
            notificationPreferences: DEFAULT_NOTIFICATION_PREFERENCES,
            notificationPolicy: reportCapablePolicy,
        },
        {
            id: DefaultBusinessRoleIdV2.MANAGER,
            name: 'Manager',
            description: 'Daily operations and staff management without device pairing.',
            grants: {
                productAreas: managerProductAreas,
                actions: managerActions,
                paymentMethods: [...businessAccess.paymentMethods],
            },
            limits: {
                maxRefundAmountCents: 100000,
                maxDiscountPercent: 50,
                canRefundOwnTransactionsOnly: false,
            },
            notificationPreferences: DEFAULT_NOTIFICATION_PREFERENCES,
            notificationPolicy: reportCapablePolicy,
        },
        {
            id: DefaultBusinessRoleIdV2.CASHIER,
            name: 'Cashier',
            description: 'Checkout, payment, customer, and basic order access.',
            grants: {
                productAreas: selectEnabled(businessAccess.productAreas, [
                    BusinessProductAreaV2.POINT_OF_SALE,
                    BusinessProductAreaV2.POINT_OF_SALE_V3,
                    BusinessProductAreaV2.POINT_OF_SALE_RESTAURANT,
                    BusinessProductAreaV2.MOBILE_PAY_TAB,
                    BusinessProductAreaV2.QUICK_PAY_V4,
                    BusinessProductAreaV2.TRANSACTIONS,
                    BusinessProductAreaV2.TRANSACTIONS_V4,
                    BusinessProductAreaV2.CUSTOMERS,
                    BusinessProductAreaV2.ORDERS,
                ]),
                actions: selectEnabled(businessAccess.actions, [
                    BusinessActionEnum.SPLIT_PAYMENT_USE,
                    BusinessActionEnum.TIP_ADD,
                    BusinessActionEnum.CUSTOMER_PROFILE_USE,
                    BusinessActionEnum.PAYMENT_CREATE,
                    ...transactionViewActions,
                    BusinessActionEnum.ORDER_VIEW,
                    BusinessActionEnum.ORDER_ACCEPT,
                    BusinessActionEnum.ORDER_COMPLETE,
                ]),
                paymentMethods: [...businessAccess.paymentMethods],
            },
            limits: {
                maxDiscountPercent: 10,
                canRefundOwnTransactionsOnly: true,
            },
            notificationPreferences: DEFAULT_NOTIFICATION_PREFERENCES,
            notificationPolicy: DEFAULT_NON_REPORT_NOTIFICATION_ROLE_POLICY,
        },
        {
            id: DefaultBusinessRoleIdV2.KITCHEN,
            name: 'Kitchen',
            description: 'Restaurant order and kitchen-display access.',
            grants: {
                productAreas: selectEnabled(businessAccess.productAreas, [
                    BusinessProductAreaV2.POINT_OF_SALE_RESTAURANT,
                    BusinessProductAreaV2.ORDERS,
                    BusinessProductAreaV2.MENU,
                    BusinessProductAreaV2.MOBILE_MENU,
                ]),
                actions: selectEnabled(businessAccess.actions, [
                    BusinessActionEnum.RESTAURANT_MEAL_FLOW,
                    BusinessActionEnum.RESTAURANT_KDS,
                    BusinessActionEnum.ORDER_VIEW,
                    BusinessActionEnum.ORDER_ACCEPT,
                    BusinessActionEnum.ORDER_COMPLETE,
                ]),
                paymentMethods: [],
            },
            limits: {
                canRefundOwnTransactionsOnly: false,
            },
            notificationPreferences: DEFAULT_NOTIFICATION_PREFERENCES,
            notificationPolicy: DEFAULT_NON_REPORT_NOTIFICATION_ROLE_POLICY,
        },
        {
            id: DefaultBusinessRoleIdV2.READ_ONLY,
            name: 'Read Only',
            description: 'Read-oriented dashboard, transaction, and report access.',
            grants: {
                productAreas: selectEnabled(businessAccess.productAreas, [
                    BusinessProductAreaV2.DASHBOARD,
                    BusinessProductAreaV2.TRANSACTIONS,
                    BusinessProductAreaV2.TRANSACTIONS_V4,
                    BusinessProductAreaV2.REPORTS,
                    BusinessProductAreaV2.MOBILE_REPORTS_TAB,
                    BusinessProductAreaV2.INVENTORY,
                    BusinessProductAreaV2.INVENTORY_V4,
                    BusinessProductAreaV2.MOBILE_INVENTORY,
                    BusinessProductAreaV2.CUSTOMERS,
                ]),
                actions: selectEnabled(businessAccess.actions, [
                    BusinessActionEnum.REPORT_VIEW,
                    ...transactionViewActions,
                    BusinessActionEnum.ORDER_VIEW,
                ]),
                paymentMethods: [],
            },
            limits: {
                canRefundOwnTransactionsOnly: false,
            },
            notificationPreferences: DEFAULT_NOTIFICATION_PREFERENCES,
            notificationPolicy: DEFAULT_NON_REPORT_NOTIFICATION_ROLE_POLICY,
        },
    ];
};
